/**
 * One queue for the warm-up work that runs behind a visible screen: the screen
 * and dialog pools, GPU warmups, the event prefetch, the menu prebuild.
 *
 * requestIdleCallback alone did not keep it off the player's frames. On Android
 * WebView the stalls are raster/GPU (Perf Session 2026-10-26: 92 of 115 hitches
 * outside JS) while the main thread looks idle, so every warm-up started at
 * once and the first ~8 s of the menu lost 3.2 s of frames. A slot goes to one
 * holder at a time, and only after the frames since the previous holder have
 * been smooth and the player has not touched the screen for a moment.
 */
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";

type Waiter = { label: string; queued_at: number; grant: ( release: () => void ) => void };

/** Frames looked at since the previous holder released the slot: at least this many, and at least SMOOTH_WINDOW_MS of them. */
const SMOOTH_FRAMES = 12;
/** At 120 Hz 12 frames are only 100 ms: dialog prewarms then started while the menu was still settling (Perf Session 2026-10-26 17:09). */
const SMOOTH_WINDOW_MS = 300;
/** Of those, this many may be ordinary hitches (an idle menu has a few), none heavy. */
const ALLOWED_HITCHES = 2;
const HITCH_MIN_MS = 20;
const HITCH_MEDIAN_FACTOR = 1.6;
const HEAVY_MIN_MS = 50;
const HEAVY_MEDIAN_FACTOR = 3;
const INPUT_QUIET_MS = 700;
/** A device that never gets smooth frames still gets its warm-ups, just later. */
const MAX_WAIT_MS = 6_000;
/** A holder that forgot to release (an exception before its finally) frees the queue. */
const MAX_HOLD_MS = 8_000;
const GRANT_IDLE_TIMEOUT_MS = 300;
const INTERVAL_WINDOW = 60;
const DISABLE_KEY = "bb_background_work_off";

export class BackgroundWork
{
  private static queue: Waiter[] = [];
  private static holder: { label: string; since: number } | null = null;
  /** Intervals since the slot was last free, newest last, at most SMOOTH_FRAMES. */
  private static recent: number[] = [];
  private static recent_ms = 0;
  private static last_frame_at = 0;
  private static intervals: number[] = [];
  private static last_input_at = 0;
  private static sampling = false;
  private static listening = false;

  /** Resolves with a release function once this caller may run; call it when the work is done. */
  public static acquire( label: string ): Promise<() => void>
  {
    if ( this.disabled() )
      return Promise.resolve( () => {} );
    this.listenForInput();
    return new Promise( resolve =>
    {
      this.queue.push( { label, queued_at: performance.now(), grant: resolve } );
      this.startSampling();
    } );
  }

  public static async run<T>( label: string, work: () => Promise<T> | T ): Promise<T>
  {
    const release = await this.acquire( label );
    try { return await work(); }
    finally { release(); }
  }

  public static describe(): { holder: string | null; queued: string[]; recent: number[] }
  {
    return { holder: this.holder?.label ?? null, queued: this.queue.map( w => w.label ), recent: this.recent.map( Math.round ) };
  }

  /** QA A/B: localStorage `bb_background_work_off` = "1" runs every warm-up at once, as before the queue. */
  private static disabled(): boolean
  {
    try { return globalThis.localStorage?.getItem( DISABLE_KEY ) === "1"; }
    catch { return false; }
  }

  private static listenForInput(): void
  {
    if ( this.listening || typeof window === "undefined" )
      return;
    this.listening = true;
    const note = (): void => { this.last_input_at = performance.now(); };
    const opts = { capture: true, passive: true };
    for ( const type of [ "pointerdown", "touchstart", "touchmove", "wheel", "keydown" ] )
      window.addEventListener( type, note, opts );
    window.addEventListener( "pointermove", e => { if ( e.buttons ) note(); }, opts );
  }

  private static startSampling(): void
  {
    if ( this.sampling || typeof requestAnimationFrame !== "function" )
      return;
    this.sampling = true;
    this.last_frame_at = 0;
    requestAnimationFrame( t => this.onFrame( t ) );
  }

  private static onFrame( now: number ): void
  {
    if ( this.last_frame_at > 0 )
      this.noteInterval( now - this.last_frame_at );
    this.last_frame_at = now;

    if ( this.holder && now - this.holder.since > MAX_HOLD_MS )
    {
      console.warn( `[BackgroundWork] ${this.holder.label} held the slot over ${MAX_HOLD_MS} ms, releasing` );
      this.holder = null;
    }

    const head = this.queue[0];
    if ( head && !this.holder && this.mayGrant( head, now ) )
      this.grant();

    if ( this.queue.length === 0 && !this.holder )
    {
      this.sampling = false;
      return;
    }
    requestAnimationFrame( t => this.onFrame( t ) );
  }

  private static noteInterval( dt: number ): void
  {
    this.intervals.push( dt );
    if ( this.intervals.length > INTERVAL_WINDOW )
      this.intervals.shift();
    // Frames count only while nobody holds the slot: the holder's own frames say nothing about the next one.
    if ( this.holder )
    {
      this.resetWindow();
      return;
    }
    this.recent.push( dt );
    this.recent_ms += dt;
    while ( this.recent.length > SMOOTH_FRAMES && this.recent_ms - this.recent[0] >= SMOOTH_WINDOW_MS )
      this.recent_ms -= this.recent.shift()!;
  }

  private static resetWindow(): void
  {
    this.recent = [];
    this.recent_ms = 0;
  }

  private static framesAreSmooth(): boolean
  {
    if ( this.recent.length < SMOOTH_FRAMES || this.recent_ms < SMOOTH_WINDOW_MS )
      return false;
    const sorted = [ ...this.intervals ].sort( ( a, b ) => a - b );
    const median = sorted[sorted.length >> 1];
    const hitch = Math.max( HITCH_MIN_MS, median * HITCH_MEDIAN_FACTOR );
    const heavy = Math.max( HEAVY_MIN_MS, median * HEAVY_MEDIAN_FACTOR );
    let hitches = 0;
    for ( const dt of this.recent )
    {
      if ( dt >= heavy )
        return false;
      if ( dt >= hitch )
        hitches++;
    }
    return hitches <= ALLOWED_HITCHES;
  }

  private static mayGrant( head: Waiter, now: number ): boolean
  {
    if ( now - head.queued_at >= MAX_WAIT_MS )
      return true;
    return this.framesAreSmooth() && now - this.last_input_at >= INPUT_QUIET_MS;
  }

  private static grant(): void
  {
    const waiter = this.queue.shift()!;
    const holder = { label: waiter.label, since: performance.now() };
    this.holder = holder;
    this.resetWindow();
    CrashBreadcrumbs.trace( "app", `bg start ${waiter.label} (waited ${Math.round( holder.since - waiter.queued_at )} ms)` );
    let released = false;
    const release = (): void =>
    {
      if ( released )
        return;
      released = true;
      CrashBreadcrumbs.trace( "app", `bg done ${holder.label} ${Math.round( performance.now() - holder.since )} ms` );
      if ( this.holder === holder )
        this.holder = null;
      if ( this.queue.length > 0 )
        this.startSampling();
    };
    // Out of the rAF callback, so the work does not delay the frame being drawn.
    const run = (): void => waiter.grant( release );
    const ric = ( globalThis as { requestIdleCallback?: ( cb: () => void, o: { timeout: number } ) => number } ).requestIdleCallback;
    if ( ric )
      ric( run, { timeout: GRANT_IDLE_TIMEOUT_MS } );
    else
      setTimeout( run, 0 );
  }
}
